<template>
  <v-card class="nx-authorization-permissions-wizard">
    <v-card-title class="headline pb-0 px-4" primary-title>
      {{ $t('message.permissions.wizard.title') }}
    </v-card-title>

    <v-card-text class="py-0 px-0">
      <v-stepper v-model="step" color="info" class="elevation-0">

        <v-stepper-header class="elevation-0">
          <v-stepper-step color="info" :complete="step > 1" step="1">
            {{ $t('message.permissions.wizard.stepper.resource') }}
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step color="info" :complete="step > 2" step="2">
            {{ $t('message.permissions.wizard.stepper.filter') }}
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step color="info" step="3">
            {{ $t('message.permissions.wizard.stepper.action') }}
          </v-stepper-step>
        </v-stepper-header>

        <v-stepper-items class="nx-authorization-permissions-wizard__content elevation-0">
          <v-stepper-content class="py-0" step="1">
            <h2 class="nx-heading--level-1">{{ stepTitle }}</h2>
            <v-radio-group v-model="resource">
              <v-radio v-for="resource in resources"
                       :key="resource"
                       :label="$t(`message.permissions.resources.${resource}`) | capitalize"
                       :value="resource"></v-radio>
            </v-radio-group>
          </v-stepper-content>
          <v-stepper-content class="py-0" min-height="520" step="2">
            <nx-authorization-permissions-wizard-filters
                v-if="isFiltersStep"
                v-model="filters"
                :availableFilters="resourceFilters(resource)">
            </nx-authorization-permissions-wizard-filters>
          </v-stepper-content>
          <v-stepper-content class="py-0" min-height="520" step="3">
            <h2 class="nx-heading--level-1">{{ stepTitle }}</h2>
            <nx-authorization-permissions-wizard-actions
                v-model="actions"
                :availableActions="resourceActions(resource)">
            </nx-authorization-permissions-wizard-actions>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-card-text>

    <v-divider></v-divider>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="secondary" small flat @click="cancel">
        {{ $t('message.permissions.buttons.cancel') }}
      </v-btn>

      <v-btn
          v-if="isFinalStep"
          color="secondary"
          small
          flat
          @click="create"
          :disabled="disabledFinalStep">
        {{ $t('message.permissions.buttons.create') }}
      </v-btn>
      <v-btn
          v-else
          color="secondary"
          small
          flat
          @click="nextStep"
          :disabled="disabledNextStep">
        {{ $t('message.permissions.buttons.continue') }}
      </v-btn>

    </v-card-actions>
  </v-card>
</template>

<script>
import { resources, resourceFilters, resourceActions } from '@/modules/authorization/config';
import WizardFilters from '@/modules/authorization/wizard/filters/Filters';
import WizardActions from '@/modules/authorization/wizard/actions/Actions';
import isEmpty from 'lodash/isEmpty';
import { toArray, cartesianProduct, flattenArrayOfObjects } from '@/utils';

// Wizard Steps
const RESOURCE = 1;
const FILTERS = 2;
const ACTIONS = 3;

export default {
  name: 'nx-authorization-permissions-wizard',
  components: {
    [WizardActions.name]: WizardActions,
    [WizardFilters.name]: WizardFilters,
  },
  props: {
    defaultResource: {
      type: String,
    },
  },
  data() {
    return {
      step: RESOURCE,
      resource: this.defaultResource,
      filters: {},
      actions: [],
    };
  },
  computed: {
    resources() {
      return resources;
    },
    stepTitle() {
      const stepToTitle = {
        [RESOURCE]: 'resource',
        [FILTERS]: 'filter',
        [ACTIONS]: 'action',
      };
      const title = stepToTitle[this.step];
      return this.$t(`message.permissions.wizard.steps.${title}`);
    },
    disabledNextStep() {
      if (!this.resource) {
        return true;
      }

      if (this.step === FILTERS
        && isEmpty(this.filters)) {
        return true;
      }

      if (this.step === ACTIONS
        && isEmpty(this.actions)) {
        return true;
      }

      return false;
    },
    disabledFinalStep() {
      if (!this.resource
        || isEmpty(this.filters)
        || isEmpty(this.actions)) {
        return true;
      }
      return false;
    },
    isFiltersStep() {
      return this.step === FILTERS;
    },
    isFinalStep() {
      return this.step === ACTIONS;
    },
  },
  methods: {
    resourceFilters(resource) {
      if (!resource) {
        return [];
      }
      if (!resourceFilters[resource]) {
        console.error(`No filters defined for resource ${resource}`);
        return [];
      }

      // Seleccionar un contenido o portada concreto no muestra el filtro de categoría
      if (resource === 'contents' && !!this.filters.content) {
        const [contentFilter] = resourceFilters[resource];
        return [contentFilter];
      }

      if (resource === 'boards' && !!this.filters.board) {
        const [boardFilter] = resourceFilters[resource];
        return [boardFilter];
      }
      return resourceFilters[resource];
    },
    resourceActions(resource) {
      if (!resource) {
        return [];
      }
      if (!resourceActions[resource]) {
        console.error(`No actions defined for resource ${resource}`);
        return [];
      }
      // La accion crear nunca debe usarse para tipos concretos
      return resourceActions[resource];
    },
    nextStep() {
      this.step = this.step + 1;
    },
    reset() {
      this.step = RESOURCE;
      this.resource = null;
      this.filters = {};
      this.actions = [];
    },
    cancel() {
      this.reset();
      this.$emit('cancel');
    },
    create() {
      /**
       * We need an array of individual permissions
       * First, transform filters object to an array of named values
       * Then, we need to apply cartesian product to obtain all the filters permutations
       * https://es.wikipedia.org/wiki/Producto_cartesiano
       */

      /*
       * First step:
       *
       * From: {
       *   type: ['article', 'video']
       *   taxonomyId: [17, 12]
       * }
       *
       * To: [
       *   [
       *     { type: 'article'},
       *     { type: 'video'}
       *   ],
       *   [
       *     { taxonomyId: 17 },
       *     { taxonomyId: 12 },
       *   ]
       * ]
       */
      const itemsToPermute = [];
      Object.entries(this.filters).forEach(([filter, filterValues]) => {
        const namedFilterValues = toArray(filterValues).map(value => ({ [filter]: value }));
        itemsToPermute.push(namedFilterValues);
      });

      /**
       * Add the actions and origin to itemsToPermute
       * ${itemsToPermute} = [
       *  ...filters,
       *  actions,
       * ]
       */
      itemsToPermute.push(this.actions.map(action => ({ action })));
      itemsToPermute.push([{ origin: 'individual' }]);

      // Get all permission permutations
      const permissions = cartesianProduct(...itemsToPermute).map(flattenArrayOfObjects);

      // Emit the result
      this.$emit('input', { [this.resource]: permissions });
      this.cancel();
    },
  },
  watch: {
    defaultResource(resource) {
      this.resource = resource;
    },
  },
};
</script>

<style lang="stylus" scoped>
.nx-authorization-permissions-wizard {
  &__content {
    min-height: 300px;
  }
}
</style>
